import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdBodygraphCenter } from '../hd-bodygraph.models';
import { HdMindCenterOption, HdMindGateOption } from './hd-mind-conditioners';

/**
 * Centrum- en poortkeuze van een mind-notitie (spec 2026-10-22-mind-logboek
 * §4, stap 1 en 2): chips van de negen centra, de open centra van de bronchart
 * vooraan en actief, de gedefinieerde gedempt maar kiesbaar; na een
 * centrumkeuze de poorten van dat centrum, de eigen activaties gedempt. Eén
 * keuze per rij, nogmaals klikken wist. Gedeeld door de momentdialoog en de
 * chart-editdialoog.
 */
@Component({
  selector: 'hd-mind-fields',
  standalone: true,
  templateUrl: './hd-mind-fields.component.html',
  styleUrl: './hd-mind-fields.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMindFieldsComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly centerOptions = input.required<readonly HdMindCenterOption[]>();
  readonly gateOptions = input<readonly HdMindGateOption[]>([]);
  readonly center = model<HdBodygraphCenter | null>(null);
  readonly gate = model<number | null>(null);

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly text = computed(() => {
    const lang = this.lang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, lang);
    return {
      centerLabel: translate('humanDesign.mindLog.fields.centerLabel'),
      gateLabel: translate('humanDesign.mindLog.fields.gateLabel'),
      openHint: translate('humanDesign.mindLog.fields.openHint'),
      definedHint: translate('humanDesign.mindLog.fields.definedHint'),
      ownGateHint: translate('humanDesign.mindLog.fields.ownGateHint'),
      centerName: (center: HdBodygraphCenter) => translate(`humanDesign.properties.centerNames.${center}`),
    };
  });

  protected toggleCenter(center: HdBodygraphCenter): void {
    const next = this.center() === center ? null : center;
    this.center.set(next);
    // Een poort hoort bij zijn centrum: bij een ander (of geen) centrum vervalt ze.
    this.gate.set(null);
  }

  protected toggleGate(gate: number): void {
    this.gate.set(this.gate() === gate ? null : gate);
  }
}
